'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { CURRICULUM, WeekData } from '@/lib/curriculum';
import { getProgress, WeekProgress } from '@/lib/storage';
import PhaseFilterChips from './PhaseFilterChips';
import WeekAccordionCard from './WeekAccordionCard';

export default function RoadmapClient() {
  const [search, setSearch] = useState('');
  const [activePhase, setActivePhase] = useState<number | null>(null);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState<Record<number, WeekProgress>>({});
  const [mounted, setMounted] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
    setProgress(getProgress());
  }, []);

  // Keyboard shortcut K = focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const refreshProgress = useCallback(() => {
    setProgress(getProgress());
  }, []);

  const toggleExpand = useCallback((weekNum: number) => {
    setExpandedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(weekNum)) next.delete(weekNum);
      else next.add(weekNum);
      return next;
    });
  }, []);

  const filteredWeeks = CURRICULUM.filter(week => {
    const matchesPhase = activePhase === null || week.phase === activePhase;
    if (!matchesPhase) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      week.title.toLowerCase().includes(q) ||
      week.phaseLabel.toLowerCase().includes(q) ||
      week.days.some(d => d.topic.toLowerCase().includes(q) || d.task.toLowerCase().includes(q)) ||
      week.assessment.toLowerCase().includes(q)
    );
  });

  const getWeekStatus = (week: WeekData): 'complete' | 'in-progress' | 'not-started' => {
    const wp = progress[week.weekNum];
    if (!wp) return 'not-started';
    if (wp.daysComplete.every(Boolean) && wp.assessmentPassed) return 'complete';
    if (wp.daysComplete.some(Boolean) || wp.assessmentPassed) return 'in-progress';
    return 'not-started';
  };

  return (
    <div style={{ padding: '32px 24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1
          style={{
            fontFamily: 'DM Serif Display, Georgia, serif',
            fontSize: '28px',
            fontWeight: 400,
            color: 'var(--text-primary)',
            marginBottom: '6px',
          }}
        >
          80-Week Roadmap
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Full-Stack AI Engineer curriculum — 6 phases, 80 weeks, 400 tasks
        </p>
      </div>

      {/* Search + Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              pointerEvents: 'none',
            }}
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search weeks, topics, tasks… (K)"
            aria-label="Search roadmap content"
            style={{
              width: '100%',
              padding: '10px 12px 10px 36px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-input)',
              color: 'var(--text-primary)',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 150ms ease',
            }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--accent-border)'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--border)'; }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: '2px',
                display: 'flex',
              }}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Phase filters */}
        <PhaseFilterChips activePhase={activePhase} onSelectPhase={setActivePhase} />
      </div>

      {/* Results count */}
      <div
        style={{
          fontSize: '12px',
          fontFamily: 'IBM Plex Mono, monospace',
          color: 'var(--text-muted)',
          marginBottom: '16px',
        }}
      >
        {filteredWeeks.length} week{filteredWeeks.length !== 1 ? 's' : ''} shown
        {search && ` · matching "${search}"`}
      </div>

      {/* Week cards */}
      {filteredWeeks.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 24px',
            gap: '12px',
            textAlign: 'center',
          }}
        >
          <Search size={32} style={{ color: 'var(--text-muted)' }} />
          <div style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-secondary)' }}>
            No weeks match your search
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '320px' }}>
            Try a different keyword or clear the phase filter
          </div>
          <button
            onClick={() => { setSearch(''); setActivePhase(null); }}
            style={{
              marginTop: '8px',
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-elevated)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredWeeks.map(week => (
            <WeekAccordionCard
              key={`week-card-${week.weekNum}`}
              week={week}
              weekProgress={mounted ? (progress[week.weekNum] ?? null) : null}
              isExpanded={expandedWeeks.has(week.weekNum)}
              status={getWeekStatus(week)}
              onToggleExpand={() => toggleExpand(week.weekNum)}
              onProgressChange={refreshProgress}
            />
          ))}
        </div>
      )}
    </div>
  );
}